import { Stack, styled, Typography, Box, Container } from '@mui/material';
import React from 'react';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '96px 0',
  backgroundColor: '#FFFFFF',
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  maxWidth: '800px',
}));
const TermsOfServiceContent: React.FC = () => (
  <SectionContainer>
    <ContentContainer>
      <Stack spacing={{ xs: 3, sm: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Last Updated: April 11, 2025
        </Typography>
        <Typography variant="body1" color="text.secondary">
          TERR.AI TERMS OF SERVICE
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please read these Terms of Service (the "Agreement") carefully. Creating an account via
          the Site or the Service (each as defined below) constitutes your consent to this
          Agreement.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This Agreement is between you and Terr.ai LIS LLC ("Company" or "we" or "us") concerning
          your use of (including any access to) the Terr.ai site currently located at
          https://terr.ai/ (together with any materials and services available therein, including
          the Service (as defined below) and successor site(s) thereto, the "Site"). This Agreement
          hereby incorporates by this reference any additional terms and conditions posted by
          Company through the Site, or otherwise made available to you by Company.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          By using the Site, you affirm that you are of legal age to enter into this Agreement or,
          if you are not, that you have obtained parental or guardian consent to enter into this
          Agreement.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          If you are an individual accessing or using the site on behalf of, or for the benefit of,
          any corporation, partnership or other entity with which you are associated (an
          "Organization"), then you are agreeing to this Agreement on behalf of yourself and such
          Organization and you represent and warrant that you have the legal authority to bind such
          Organization to this Agreement. References to "you" and "your" in this Agreement will
          refer to both the individual using the Site and to any such Organization.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This Agreement contains a mandatory arbitration provision that, as further set forth in
          Section 15 below, requires the use of arbitration on an individual basis to resolve
          disputes, rather than jury trials or any other court proceedings, or class actions of any
          kind.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Changes.</b> We may change this Agreement from time to time by notifying you of such
          changes by any reasonable means, including by posting a revised Agreement through the
          Site. Any such changes will not apply to any dispute between you and us arising prior to
          the date on which we posted the revised Agreement incorporating such changes, or otherwise
          notified you of such changes.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your use of the Site following any changes to this Agreement will constitute your
          acceptance of such changes. The "Last Updated" legend above indicates when this Agreement
          was last changed. We may, at any time and without liability, modify or discontinue all or
          part of the Site (including access to the Site via any third-party links); charge, modify
          or waive any fees required to use the Site; or offer opportunities to some or all Site
          users.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Information Submitted Through the Site.</b> Your submission of information through the
          Site is governed by Company's Privacy Policy, located at
          https://www.terr.ai/privacy-policy (the "Privacy Policy"). You represent and warrant that
          any information you provide in connection with the Site is and will remain accurate and
          complete, and that you will maintain and update such information as needed.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Jurisdictional Issues.</b> The Site is controlled or operated (or both) from the United
          States, and is not intended to subject Company to any non-U.S. jurisdiction or law. The
          Site may not be appropriate or available for use in some non-U.S. jurisdictions. Any use
          of the Site is at your own risk, and you must comply with all applicable laws, rules and
          regulations in doing so. We may limit the Site's availability at any time, in whole or in
          part, to any person, geographic area or jurisdiction that we choose.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Your Limited Rights.</b> Subject to your compliance with this Agreement, and solely for
          so long as you are permitted by Company to use the Site, you may view one (1) copy of any
          portion of the Site to which we provide you access under this Agreement, on any single
          device, solely for your personal, non-commercial use.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Service.</b>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Through the Site, Company makes available a property management platform powered by
          generative artificial intelligence that can be accessed by users to manage properties and
          communicate with tenants (the "Service"). You understand and agree that Company is not
          party to or involved in any way in any engagement or agreement between you and your
          tenants and you are solely responsible for your relationships and engagements with your
          tenants, including any duties imposed by applicable law on a landlord and/or property
          manager.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Company may at any time in its sole discretion modify, limit the availability of,
          discontinue, impose conditions on, or refuse to make available to any user the Service or
          any feature(s) thereof.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Beta Services.</b> Company may in its sole discretion make available certain
          pre-release features as part of the Services ("Beta Services") and Company may discontinue
          Beta Services at any time in its sole discretion or never make them generally available.
          Company shall have no liability arising out of or in connection with a Beta Service.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Free Trials.</b> Company may offer free trial access to and use of the Services (such
          trial, the "Free Trial", and such period, the "Free Trial Period"). The length of any such
          Free Trial Period may vary from time to time in Company's sole discretion and will expire
          at midnight Eastern Time on the last day of the Free Trial Period.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Registration.</b> You must register in order to access or use the Service. We may
          reject, or require that you change, any username, password or other information that you
          provide to us in registering. Your username and password are for your personal use only
          and should be kept confidential; you, and not Company, are responsible for any use or
          misuse of your username or password, and you must promptly notify us of any
          confidentiality breach or unauthorized use of your username or password, or your Site
          account.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Subscriptions.</b> You may be required to purchase a subscription to use all or part of
          our Services ("Subscription"), which may require enrollment in a payment plan involving
          automatic renewal (a "Recurring Subscription"). If you purchase such a Subscription, you
          authorize Company to store your payment method information and charge that payment method
          automatically upon the renewal for the rate of your Recurring Subscription, plus
          applicable taxes, with no further action required by you. YOU REPRESENT AND WARRANT THAT
          YOU HAVE THE RIGHT TO USE THE PAYMENT METHOD THAT YOU SUBMIT IN CONNECTION WITH A
          SUBSCRIPTION. The length of your Recurring Subscription will be provided when you make
          your purchase. Your Recurring Subscription will automatically renew unless you cancel it.
          In the event that Company is unable to charge your payment method as authorized by you
          when you enrolled in a Subscription, Company may in its sole discretion (a) bill you for
          the Subscription and suspend your access to the Services until payment is received; or (b)
          seek to update your payment method information through third-party sources (i.e., your
          bank or a payment processor) to continue charging your account as authorized by you. If
          your primary payment method fails, you authorize us to charge any other payment method
          associated with your account. If we are unable to obtain payment from you for any reason,
          we may suspend your Subscription.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Cancelling your Subscription.</b> You may cancel your Subscription and terminate your
          account through the Service. You may cancel a Subscription at any time, but if you cancel
          your Subscription before the end of the current subscription period, we will not refund
          any subscription fees already paid to us. Following any cancellation, however, you will
          continue to have access to the applicable Services through the end of your current
          subscription period. Company may change the prices charged for Recurring Subscriptions at
          any time by posting updated pricing through the Services; provided, however, that the
          prices for your Recurring Subscription will remain in force for the duration of the
          subscription period for which you have paid. After that period ends, your use of the
          applicable Services will be charged at the then- current subscription price. Company
          reserves the right, including without prior notice, to impose conditions on the honoring
          of any coupon, discount or similar promotion; and to bar any user from purchasing a
          Subscription. Subscription fees are non-refundable.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Data Removal Requests.</b> If you cancel your Subscription and terminate your account,
          you may request that Company delete any personal information you have submitted to the
          Service via the Site. Notwithstanding any deletion request, you acknowledge and agree that
          Company may retain such information in a de-identified manner and use such information in
          accordance with the license grant set forth in Section 5.10 below.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Communications.</b> The Service may enable you to communicate with tenants, including
          via text and email and may enable you to send your tenants automated communications
          generated by the Service based on your account settings, prior messages with tenants,
          other Inputs (as defined below) and communications from other users of the Service. You
          are responsible for all communications with your tenants and any materials you send to
          your tenants via the Service, including for ensuring such communications and materials are
          accurate and complete and you agree to implement and distribute to your tenants a privacy
          policy that describes the information you and your service providers (e.g., Company)
          collect from your tenants and how such information is used.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Content.</b> You may provide input to the Service, including information about your
          properties and tenants and your communications with tenants ("Input"), and receive output
          from the Services based on the Input, including the automated communications referenced in
          Section 5.6 above ("Output"), which may then be provided to tenants via text or email
          depending on your account settings. Input and Output are collectively "Content". You are
          responsible for Content, including for ensuring that it does not violate any applicable
          law or these Terms. You represent and warrant that you have all rights, licenses, and
          permissions needed to provide Input to our Services, including any necessary rights,
          licenses, and permissions from your tenants. As between you and Company, you retain your
          ownership rights in Input. Due to the nature of the Service and artificial intelligence
          generally, Output may not be unique and other users and their tenants may receive similar
          output from the Service. If you choose to make any of your information publicly available
          through the Service or otherwise, you do so at your own risk.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>License.</b> By submitting Input to the Service, you hereby grant to Company a
          worldwide, perpetual, irrevocable, transferable, sublicensable (through multiple tiers),
          non-exclusive right and license to reproduce, perform and display (publicly or otherwise),
          edit, improve, modify, adapt, publish, translate, create derivative works based on,
          distribute and otherwise use such Input in connection with the Site and Service, including
          to generate Outputs and to provide, develop, maintain and improve the Service and to
          develop new services and products.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Disclaimers.</b> WITHOUT LIMITING THE GENERALITY OF THE DISCLAIMERS SET FORTH IN
          SECTION 10 BELOW, YOU HEREBY ACKNOWLEDGE AND AGREE: (A) OUTPUT MAY NOT ALWAYS BE ACCURATE
          AND NEITHER YOU NOR YOUR TENANTS SHOULD RELY ON OUTPUT FROM THE SERVICE AS A SOLE SOURCE
          OF TRUTH OR FACTUAL INFORMATION, OR AS A SUBSTITUTE FOR PROFESSIONAL ADVICE; (B) YOU WILL
          EVALUATE OUTPUT FOR ACCURACY AND APPROPRIATENESS FOR YOUR USE CASE, INCLUDING USING HUMAN
          REVIEW AS APPROPRIATE, BEFORE USING OR SHARING OUTPUT; (C) YOU WILL NOT USE ANY OUTPUT
          RELATING TO A PERSON OR ANY OTHER INFORMATION OR MATERIALS MADE AVAILABLE ON THE SITE OR
          SERVICE FOR ANY PURPOSE THAT COULD HAVE A LEGAL OR MATERIAL IMPACT ON THAT PERSON, SUCH AS
          MAKING CREDIT, EDUCATIONAL, EMPLOYMENT, HOUSING, INSURANCE, LEGAL, MEDICAL, OR OTHER
          IMPORTANT DECISIONS ABOUT THEM; AND (D) THE SERVICE MAY PROVIDE INCOMPLETE, INCORRECT OR
          OFFENSIVE OUTPUT THAT DOES NOT REPRESENT COMPANY'S VIEWS. IF OUTPUT REFERENCES ANY THIRD
          PARTY PRODUCTS OR SERVICES, IT DOES NOT MEAN THAT THE THIRD PARTY ENDORSES OR IS
          AFFILIATED WITH COMPANY.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Rules of Conduct.</b> In connection with the Site, you must not:
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Post, transmit or otherwise make available through or in connection with the Site any
          materials that are or may be: (a) threatening, harassing, degrading, hateful or
          intimidating, or otherwise fail to respect the rights and dignity of others; (b)
          defamatory, libelous, fraudulent or otherwise tortious; (c) obscene, indecent,
          pornographic or otherwise objectionable; or (d) protected by copyright, trademark, trade
          secret, right of publicity or privacy or any other proprietary right, without the express
          prior written consent of the applicable owner.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Post, transmit or otherwise make available through or in connection with the Site any
          virus, worm, Trojan horse, Easter egg, time bomb, spyware or other computer code, file or
          program that is or is potentially harmful or invasive or intended to damage or hijack the
          operation of, or to monitor the use of, any hardware, software or equipment (each, a
          "Virus").
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Use the Site for any purpose that is fraudulent or otherwise tortious or unlawful.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Harvest or collect information about users of the Site.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Interfere with or disrupt the operation of the Site or the servers or networks used to
          make the Site available, including by hacking or defacing any portion of the Site; or
          violate any requirement, procedure or policy of such servers or networks.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Restrict or inhibit any other person from using the Site.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Reproduce, modify, adapt, translate, create derivative works of, sell, rent, lease, loan,
          timeshare, distribute or otherwise exploit any portion of (or any use of) the Site except
          as expressly authorized herein, without Company's express prior written consent.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Reverse engineer, decompile or disassemble any portion of the Site, except where such
          restriction is expressly prohibited by applicable law.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Remove any copyright, trademark or other proprietary rights notice from the Site.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Frame or mirror any portion of the Site, or otherwise incorporate any portion of the Site
          into any product or service, without Company's express prior written consent.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Systematically download and store Site content.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Use any robot, spider, site search/retrieval application or other manual or automatic
          device to retrieve, index, "scrape," "data mine" or otherwise gather Site content, or
          reproduce or circumvent the navigational structure or presentation of the Site, without
          Company's express prior written consent. Notwithstanding the foregoing, and subject to
          compliance with any instructions posted in the robots.txt file located in the Site's root
          directory, Company grants to the operators of public search engines permission to use
          spiders to copy materials from the Site for the sole purpose of (and solely to the extent
          necessary for) creating publicly available, searchable indices of such materials, but not
          caches or archives of such materials. Company reserves the right to revoke such permission
          either generally or in specific cases, at any time and without notice.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You are responsible for obtaining, maintaining and paying for all hardware and all
          telecommunications and other services needed to use the Site.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Feedback.</b> In addition, if you provide to us any ideas, proposals, suggestions or
          other materials ("Feedback"), whether related to the Site or otherwise, you hereby
          acknowledge and agree that such Feedback is not confidential, and that your provision of
          such Feedback is gratuitous, unsolicited and without restriction, and does not place
          Company under any fiduciary or other obligation.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You represent and warrant that you have all rights necessary to grant the licenses granted
          in this section, and that your Feedback, and your provision thereof through and in
          connection with the Site, are complete and accurate, and are not fraudulent, tortious or
          otherwise in violation of any applicable law or any right of any third party. You further
          irrevocably waive any "moral rights" or other rights with respect to attribution of
          authorship or integrity of materials regarding any Feedback that you may have under any
          applicable law under any legal theory.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Company's Proprietary Rights.</b> We and our suppliers own the Site, which is protected
          by proprietary rights and laws. Our trade names, trademarks and service marks include
          TERR.AI and any associated logos. All trade names, trademarks, service marks and logos on
          the Site not owned by us are the property of their respective owners. You may not use our
          trade names, trademarks, service marks or logos in connection with any product or service
          that is not ours, or in any manner that is likely to cause confusion. Nothing contained on
          the Site should be construed as granting any right to use any trade names, trademarks,
          service marks or logos without the express prior written consent of the owner.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Third Party Materials.</b>
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Generally.</b> Certain Site functionality may make available access to information,
          products, services and other materials made available by third parties, including
          Submissions and the RLI Services described in Section 9.2 below ("Third Party Materials"),
          or allow for the routing or transmission of such Third Party Materials, including via
          links. By using such functionality, you are directing us to access, route and transmit to
          you the applicable Third Party Materials.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          We neither control nor endorse, nor are we responsible for, any Third Party Materials,
          including the accuracy, validity, timeliness, completeness, reliability, integrity,
          quality, legality, usefulness or safety of Third Party Materials, or any intellectual
          property rights therein. Certain Third Party Materials may, among other things, be
          inaccurate, misleading or deceptive. Nothing in this Agreement shall be deemed to be a
          representation or warranty by Company with respect to any Third Party Materials. We have
          no obligation to monitor Third Party Materials, and we may block or disable access to any
          Third Party Materials (in whole or part) through the Site at any time. In addition, the
          availability of any Third Party Materials through the Site does not imply our endorsement
          of, or our affiliation with, any provider of such Third Party Materials, nor does such
          availability create any legal relationship between you and any such provider.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          YOUR USE OF THIRD PARTY MATERIALS IS AT YOUR OWN RISK AND IS SUBJECT TO ANY ADDITIONAL
          TERMS, CONDITIONS AND POLICIES APPLICABLE TO SUCH THIRD PARTY MATERIALS (SUCH AS TERMS OF
          SERVICE OR PRIVACY POLICIES OF THE PROVIDERS OF SUCH THIRD PARTY MATERIALS).
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>RLI Services.</b> You may be able to access certain services and technology that
          Company licenses from Rocket Lawyer Incorporation ("RLI") via the Site, including a lease
          generation service provided by RLI (the "RLI Services"). You acknowledge and agree that:
          (a) the RLI Services are Third Party Materials as defined above and RLI is not a party to
          this Agreement; (b) any information Company collects, stores or processes from you or the
          systems you use to access or deploy the RLI Services is subject to this Agreement; (c) the
          use restrictions set forth in Section 6 above apply to the RLI Services; (d) each of
          Company and RLI shall maintain all rights, title and interest in and to its respective
          patents, inventions, copyrights, trademarks, domain names, trade secrets, know-how and any
          other intellectual property and/or proprietary rights and the rights granted to you under
          this Agreement do not convey any additional rights in the RLI Services or any intellectual
          property and/ or proprietary rights associated therewith; and (e) in the course of
          providing certain technology for the RLI Services, RLI reserves the right to collect data
          and information created or entered by you or on your behalf (including, without
          limitation, API calls and metadata, drafts, documents, signatures, payment information,
          questions and answers), and maintain such data and information in connection with your
          current or prospective RLI account and RLI's collection and use of such data and
          information is subject to RLI's privacy policy and not Company's privacy policy. RLI shall
          be deemed a third party beneficiary of this Agreement solely with regard to subsections
          (d) and (e) above.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Disclaimer of Warranties.</b> To the fullest extent permitted under applicable law: (a)
          the Site and Third Party Materials are made available to you on an "As Is," "Where Is" and
          "Where Available" basis, without any warranties of any kind, whether express, implied or
          statutory; and (b) Company disclaims all warranties with respect to the Site and Third
          Party Materials, including the warranties of merchantability, fitness for a particular
          purpose, non-infringement and title. All disclaimers of any kind (including in purpose,
          non-infringement and title. All disclaimers of any kind (including in this section and
          elsewhere in this Agreement) are made for the benefit of both Company and its affiliates
          and their respective directors, officers, employees, affiliates, agents, representatives,
          licensors, suppliers and service providers (collectively, the "Affiliated Entities"), and
          their respective successors and assigns.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          While we try to maintain the timeliness, integrity and security of the Site, we do not
          guarantee that the Site is or will remain updated, complete, correct or secure, or that
          access to the Site will be uninterrupted. The Site may include inaccuracies, errors and
          materials that violate or conflict with this Agreement. Additionally, third parties may
          make unauthorized alterations to the Site. If you become aware of any such alteration,
          contact us at hello@terr.ai with a description of such alteration and its location on the
          Site.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Limitation of Liability.</b> To the fullest extent permitted under applicable law:
          (a)Company will not be liable for any indirect, incidental, consequential, special,
          exemplary or punitive damages of any kind, under any contract, tort (including
          negligence), strict liability or other theory, including damages for loss of profits, use
          or data, loss of other intangibles, even if advised in advance of the possibility of such
          damages or losses; (b) without limiting the foregoing, Company will not be liable for
          damages of any kind resulting from your use of or inability to use the Site or Third Party
          Materials, including from any Virus that may be transmitted in connection therewith; (c)
          your sole and exclusive remedy for dissatisfaction with the Site or any Products or Third
          Party Materials is to stop using the Site; and (d) the maximum aggregate liability of
          Company for all damages, losses and causes of action, whether in contract, tort (including
          negligence) or otherwise, shall be the greater of (i) the total amount, if any, paid by
          you to Company for use of the Services during the twelve (12) months immediately preceding
          the event giving rise to the claim, and (ii) one hundred U.S. dollars ($100.00). All
          limitations of liability of any kind (including in this section and elsewhere in this
          Agreement) are made for the benefit of both Company and the Affiliated Entities, and their
          respective successors and assigns.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Indemnity.</b> To the fullest extent permitted under applicable law, you agree to
          defend, indemnify and hold harmless Company and the Affiliated Entities, and their
          respective successors and assigns, from and against all claims, liabilities, damages,
          judgments, awards, losses, costs, expenses and fees (including attorneys' fees) arising
          out of or relating to (a) your use of, or activities in connection with, the Site
          (including your use of the Services and any Content); (b) your Inputs and any Outputs
          based on such Inputs; (c) any disputes between you and your tenant(s) or other third
          parties; and (d) any violation or alleged violation of this Agreement by you.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Termination.</b> This Agreement is effective until terminated. Company may terminate or
          suspend your use of the Site at any time and without prior notice, for any or no reason,
          including if Company believes that you have violated or acted inconsistently with the
          letter or spirit of this Agreement. Upon any such termination or suspension, your right to
          use the Site will immediately cease, and Company may, without liability to you or any
          third party, immediately deactivate or delete your user name, password and account, and
          all associated materials, without any obligation to provide any further access to such
          materials. Sections 2, 3, 5.4, 5.5, 5.6, 6–19 shall survive any expiration or termination
          of this Agreement.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Governing Law.</b> The terms of this Agreement are governed by the laws of the United
          States (including federal arbitration law) and the state of New York without regard to its
          principles of conflicts of law, and regardless of your location.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Dispute Resolution.</b> Any dispute arising out of or related to this Agreement or the
          Site, including a dispute involving the alleged breach, termination or validity of the
          Agreement, will be resolved through arbitration before a single neutral arbitrator instead
          of in a court by a judge or jury, and the parties agree that they are each waiving any
          right to a trial by jury. The arbitration will be administered by the American Arbitration
          Association under its Commercial Arbitration Rules then in effect, as amended by this
          Agreement. Any in-person hearings or appearances will be held in New York, New York. The
          arbitrator's decision will follow the terms of this Agreement and will be final and
          binding. The arbitrator will have the authority to award temporary, interim or permanent
          injunctive relief or relief providing for specific performance of this Agreement, but only
          to the extent necessary to provide relief warranted by the individual claim(s) before the
          arbitrator. The award rendered by the arbitrator may be confirmed and enforced in any
          court having jurisdiction thereof (the parties acknowledge and agree that any court
          located in the Borough of Manhattan in New York, New York shall have jurisdiction
          thereof).
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Information or Complaints.</b> If you have a question or complaint regarding the Site,
          please send an e-mail to hello@terr.ai. You may also contact us by writing to PO Box 3067,
          Princeton NJ 08543. Please note that e-mail communications will not necessarily be secure;
          accordingly you should not include credit card information or other sensitive information
          in your e-mail correspondence with us.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Copyright Infringement Claims.</b> The Digital Millennium Copyright Act of 1998 (the
          "DMCA") provides recourse for copyright owners who believe that material appearing on the
          Internet infringes their rights under U.S. copyright law. If you believe in good faith
          that materials available on the Site infringe your copyright, you (or your agent) may send
          to Company a written notice by mail or e-mail, requesting that Company remove such
          material or block access to it. If you believe in good faith that someone has wrongly
          filed a notice of copyright infringement against you, the DMCA permits you to send to
          Company a counter-notice. Notices and counter-notices must meet the then-current statutory
          requirements imposed by the DMCA. See http://www.copyright.gov/ for details. Notices and
          counter-notices must be sent in writing to Company as follows: By mail to Terr.ai LIS LLC,
          PO Box 3067, Princeton NJ 08543, or by e-mail to hello@terr.ai.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          We suggest that you consult your legal advisor before filing a DMCA notice or
          counter-notice.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Export Controls.</b> You are responsible for complying with United States export
          controls and for any violation of such controls, including any United States embargoes or
          other federal rules and regulations restricting exports. You represent, warrant and
          covenant that you are not (a) located in, or a resident or a national of, any country
          subject to a U.S. government embargo or other restriction, or that has been designated by
          the U.S. government as a "terrorist supporting" country; or (b) on any of the U.S.
          government lists of restricted end users.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <b>Miscellaneous.</b> This Agreement does not, and shall not be construed to, create any
          partnership, joint venture, employer-employee, agency or franchisor-franchisee
          relationship between you and Company. If any provision of this Agreement is found to be
          unlawful, void or for any reason unenforceable, that provision will be deemed severable
          from this Agreement and will not affect the validity and enforceability of any remaining
          provision. You may not assign, transfer or sublicense any or all of your rights or
          obligations under this Agreement without our express prior written consent. We may assign,
          transfer or sublicense any or all of our rights or obligations under this Agreement
          without restriction. No waiver by either party of any breach or default under this
          Agreement will be deemed to be a waiver of any preceding or subsequent breach or default.
          Any heading, caption or section title contained herein is for convenience only, and in no
          way defines or explains any section or provision. All terms defined in the singular shall
          have the same meanings when used in the plural, where appropriate and unless otherwise
          specified. Any use of the term "including" or variations thereof in this Agreement shall
          be construed as if followed by the phrase "without limitation." This Agreement, including
          any terms and conditions incorporated herein, is the entire agreement between you and
          Company relating to the subject matter hereof, and supersedes any and all prior or
          contemporaneous written or oral agreements or understandings between you and Company
          relating to such subject matter. Notices to you (including notices of changes to this
          Agreement) may be made via posting to the Site or by e-mail (including in each case via
          links), or by regular mail. Without limitation, a printed version of this Agreement and of
          any notice given in electronic form shall be admissible in judicial or administrative
          proceedings based upon or relating to this Agreement to the same extent and subject to the
          same conditions as other business documents and records originally generated and
          maintained in printed form. Company will not be responsible for any failure to fulfill any
          obligation due to any cause beyond its control.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Site ©2023 - 2025 Terr.ai LIS LCC, unless otherwise noted. All rights reserved.
        </Typography>
      </Stack>
    </ContentContainer>
  </SectionContainer>
);

export default TermsOfServiceContent;
